<template>
  <TagManageDialog
    v-model:visibility="dialogManageVisible"
    :tags="availableTags"
    @tag-changed="loadTags" />

  <TagCreateDialog
    v-model:visibility="dialogCreateVisible"
    @saved="(newTag) => handleNewTagCreated(newTag)"
    @closed="newTagPerson = null" />

  <PeopleDrawer
    v-if="personDetailsDrawerId"
    :key="personDetailsDrawerId"
    v-model:drawer-visibility="personDetailsDrawerVisibility"
    :person-id="personDetailsDrawerId"
    @close="personDetailsDrawerId = null" />

  <PeopleCreateDialog
    v-model:visible="createPersonDrawerVisibility"
    :all-software-licenses
    :all-contracts
    :all-apps="allApplications"
    :all-tags="availableTags" />

  <PeopleDeleteDialog
    v-model:visibility="deletePeopleDialogVisibility"
    :person="peopleToDelete[0]" />

  <main class="flex flex-col gap-6">
    <div class="flex">
      <div class="table-controls-container w-full">
        <!-- Search -->
        <SmInput
          v-model="searchTerm"
          outline
          style="width: 300px"
          size="medium"
          :label="i18n.t('views.users.table.searchPlaceholder')" />

        <!-- Filter displayed people -->
        <PeopleFilter v-model:model-value="filter" />

        <SmButton
          v-require-permission="CompanyAccessRole.ADMIN"
          class="ml-auto"
          data-tutorial-id="people-import-people-button"
          @click="createPersonDrawerVisibility = true">
          {{ i18n.t('views.users.userAddButton') }}
        </SmButton>
      </div>
    </div>

    <el-card
      shadow="always"
      data-tutorial-id="people-list"
      style="overflow-y: auto"
      class="grow"
      :body-style="{
        height: '100%',
        width: '100%',
      }">
      <!-- People Table -->
      <SmTable
        v-if="loadPeople || people.length"
        ref="tableRef"
        :data="people"
        :columns="columns"
        :loading="loadPeople"
        key-field="_id"
        :selected="selectedPeople"
        :contain-strict="true"
        @cell-click="handleRowClick"
        @scroll-end-reached="loadMorePersons">
        <!-- Application Count -->
        <template #applicationCount="{ row }">
          <div class="application-count">
            <TextMultiline
              :text="
                i18n.t('apps', {
                  count: (row as EnrichedAccount).application_count,
                })
              " />
          </div>
        </template>
        <!-- Employees Indicator -->
        <template #employees="{ row }">
          <div
            class="employee-indicator"
            :class="{
              'employee-indicator--linked':
                Array.isArray(row.employee_ids) && row.employee_ids.length > 0,
            }">
            <Link2 :size="14" />
            <span v-if="row.employee_ids?.length">
              {{ row.employee_ids.length }}
            </span>
            <span v-else>—</span>
          </div>
        </template>
        <template #action="{ row }">
          <SmDropdown trigger="click">
            <!-- Show Person -->
            <SmDropdownItem :icon="User" @click="selectPerson(row)">
              {{ i18n.t('showUser') }}
            </SmDropdownItem>

            <!-- Edit Person -->
            <SmDropdownItem :icon="PencilLine" @click="selectPerson(row)">
              {{ i18n.t('editUser') }}
            </SmDropdownItem>

            <!-- Delete License -->
            <SmDropdownItem
              v-require-permission="CompanyAccessRole.ADMIN"
              :icon="Trash"
              hover-color="var(--sm-magenta)"
              @click="handleDeletePersonClick(row)">
              {{ i18n.t('deleteUser') }}
            </SmDropdownItem>
          </SmDropdown>
        </template>

        <!-- Groups -->
        <template #groups="{ row }">
          <div class="tag-wrapper">
            <TagComponent
              v-for="group in getGroupsByAccountId(row._id)"
              :key="group._id"
              :tag="{
                name: group.name,
                color: {
                  ...generateFallbackColor(),
                  ...group.color,
                },
              }"
              :selected="selectedGroups.includes(group)"
              @click.capture="handleGroupsClick(group)" />

            <TagSelect
              show-create-tag
              :available-tags="availableGroups"
              :selected="getSelectedGroupsIds(row._id)"
              create-i18n-key="newGroup"
              @delete-tag="(groupID) => removePersonFromGroup(groupID, row._id)"
              @add-tag="(groupID) => addPersonToAccountGroup(groupID, row._id)"
              @create-tag="handleCreateGroup">
              <Plus :size="16" class="tag-add-icon" />
            </TagSelect>
          </div>
        </template>

        <!-- Tags -->
        <template #header-tags>
          <div class="flex items-center gap-2">
            {{ i18n.t('tags') }}
            <Settings
              :size="16"
              class="cursor-pointer text-contrast hover:text-primary"
              @click="dialogManageVisible = true" />
          </div>
        </template>

        <template #tags="{ row }">
          <div
            class="flex w-full cursor-default flex-wrap items-center gap-2 py-2">
            <TagComponent
              v-for="tag in getTagsByIds(row.tags)"
              :key="tag._id"
              :tag="tag"
              :selected="selectedTags.includes(tag)"
              :style="{ maxWidth: 'calc(100% - 2rem)' }"
              class="cursor-pointer"
              @click.capture="handleTagClick(tag)" />
            <TagSelect
              show-create-tag
              :available-tags="availableTags"
              :selected="row.tags"
              @delete-tag="removeTagFromPerson($event, row.tags, row._id)"
              @add-tag="addTagToPerson($event, row.tags, row._id)"
              @create-tag="openCreateTagDialog(row)">
              <Plus :size="16" class="tag-add-icon opacity-0 transition" />
            </TagSelect>
          </div>
        </template>

        <!-- Created -->
        <template #created="{ row }">
          <span v-if="row.created">
            {{ displayDate(new Date(row.created), { dateStyle: 'short' }) }}
          </span>
          <span
            v-else
            style="font-weight: 500; color: var(--el-text-color-disabled)">
            {{ i18n.t('views.licences.table.neverLoggedIn') }}
          </span>
        </template>
      </SmTable>

      <EmptyState
        v-else-if="!loadPeople && people.length === 0"
        :icon="User"
        :title="i18n.t('personEmptyStateTitle')"
        :text="i18n.t('personEmptyStateText')"
        :create-label="i18n.t('views.users.userAddButton')"
        :show-docs-btn="false"
        class="empty-state"
        @create="createPersonDrawerVisibility = true" />
    </el-card>
  </main>
</template>

<script lang="ts" setup>
  import { ComponentExposed } from 'vue-component-type-helpers'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import { useDebounceFn } from '@vueuse/core'
  import { generateFallbackColor } from '@/utils/colors'

  import {
    AccountGroup,
    Account,
    AccountUpdateIn,
    CompanyAccessRole,
    PriceInformation,
    SoftwareLicense,
    Tag,
    TagsService,
    UserGroupsService,
    SoftwareLicensesService,
    SoftwareOut,
  } from '@/client'
  import { useSortTableColumn } from '@/composables/useSortTableColumn'
  import SmTable from '@/components/sm/SmTable.vue'
  import router from '@/router'
  import { Column } from '@/components/sm/SmTable.types'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { displayDate } from '@/utils/formatter/date'
  import { EnrichedAccount, PersonStore } from '@/stores/person/person.store'
  import { Link2, PencilLine, Plus, Settings, Trash, User } from 'lucide-vue-next'
  import { PersonFilters } from '@/stores/person/person.filter'

  const route = useRoute()
  const i18n = useI18n()

  const allApplications = ApplicationStore.useApplications<SoftwareOut>().data
  const allSoftwareLicenses = ref<SoftwareLicense[]>([])
  const allContracts = ref<PriceInformation[]>([])

  const tableRef = ref<ComponentExposed<typeof SmTable<Account>> | null>(null)

  /**
   * Table related stuff
   */

  // Load people

  function fetchSoftwareLicenses() {
    SoftwareLicensesService.getSoftwareLicensesApiV1SoftwareSoftwareLicensesGet(
      {}
    ).then((res) => {
      allSoftwareLicenses.value = res
    })
    SoftwareLicensesService.getSoftwareLicensePriceInformationsApiV1SoftwareSoftwareLicensePriceInformationsGet(
      {}
    ).then((res) => {
      allContracts.value = res
    })
  }

  const filter = ref<PersonFilters>({
    search: undefined,
  })

  const searchTerm = ref(filter.value.search ?? '')

  const applySearchFilter = useDebounceFn((raw: string) => {
    const trimmed = raw.trim()
    filter.value.search = trimmed ? trimmed : undefined
  }, 180)

  watch(searchTerm, (value) => {
    applySearchFilter(value)
  })

  watch(
    () => filter.value.search,
    (next) => {
      const normalized = next ?? ''
      if (normalized !== searchTerm.value) {
        searchTerm.value = normalized
      }
    }
  )

  // ######################################
  // ############# Pagination #############
  // ######################################

  const INITIAL_ROW_LIMIT = 300
  const ROW_EXPAND_COUNT = 100

  const {
    data: people,
    updateParameters,
    loading,
  } = PersonStore.usePersons<EnrichedAccount>(
    filter,
    { limit: INITIAL_ROW_LIMIT },
    { column: 'email' },
    true
  )

  const { count: countFilteredPersons } = PersonStore.usePersonsCount(filter)
  const { count: countPersons } = PersonStore.usePersonsCount()

  async function loadMorePersons() {
    updateParameters({}, { limit: people.value.length + ROW_EXPAND_COUNT })
  }

  const loadPeople = computed(() => loading.value && people.value.length === 0)

  function handleRowClick(iRow: number, columnKey: string, row: Account) {
    if (columnKey === 'selection') return
    if (columnKey === 'action') return
    if (columnKey === 'templates') return
    if (columnKey === 'tags') return
    if (columnKey === 'groups') return
    selectPerson(row)
  }

  const selectedPeople = ref<Account[]>([])

  /**
   * Tag Filter
   */
  const currentTagFilter = ref()
  currentTagFilter.value = route.query.tag_filter
    ? route.query.tag_filter
    : false

  /**
   * Table Button Functions
   */

  const selectPerson = (clickedPerson: Account) => {
    personDetailsDrawerId.value = clickedPerson._id
    personDetailsDrawerVisibility.value = true
  }

  const peopleToDelete = ref<EnrichedAccount[]>([])
  const handleDeletePersonClick = (person: EnrichedAccount) => {
    peopleToDelete.value = [person]
    deletePeopleDialogVisibility.value = true
  }

  const personFooterCount = computed(() => {
    if (countPersons.value != countFilteredPersons.value) {
      return `${i18n.t('XofY', { x: countFilteredPersons.value, y: countPersons.value })}  ${i18n.t('person', { count: countPersons.value })}`
    } else {
      return `${countPersons.value} ${i18n.t('person', { count: countPersons.value })}`
    }
  })

  watch(
    () => (tableRef.value ? tableRef.value.selection : []),
    (selection: Account[]) => {
      selectedPeople.value = selection
    }
  )

  /**
   * Drawer Visibilities and the custom data for  drawer / dialog
   */
  const createPersonDrawerVisibility = ref(false)
  const personDetailsDrawerVisibility = ref(false)
  const personDetailsDrawerId = ref<string | null>(null)
  const deletePeopleDialogVisibility = ref<boolean>(false)

  watchEffect(() => {
    if (route.query['open-add-person-drawer']) {
      createPersonDrawerVisibility.value = route.query['open-add-person-drawer']
        ? route.query['open-add-person-drawer'] === 'true'
        : false
    }

    if (route.query['open-detail-drawer']) {
      personDetailsDrawerVisibility.value = route.query['open-detail-drawer']
        ? route.query['open-detail-drawer'] === 'true'
        : false
    }
    if (route.query['open-detail-drawer-email']) {
      personDetailsDrawerId.value =
        route.query['open-detail-drawer-email'].toString()
    }
  })

  // Watch the drawer visibilities to refetch the people
  watch([personDetailsDrawerVisibility, createPersonDrawerVisibility], () => {
    if (
      !personDetailsDrawerVisibility.value &&
      !createPersonDrawerVisibility.value
    ) {
      // fetchPeople()
    }
  })

  // ##############################################
  // Group related stuff
  // ##############################################

  const availableGroups = ref<AccountGroup[]>([])
  const selectedGroups = ref<AccountGroup[]>([])

  function loadGroups() {
    UserGroupsService.getAccountGroupsApiV1ManagementGroupsGet({}).then(
      (groups) => {
        availableGroups.value = groups
      }
    )
  }

  function getGroupsByAccountId(accountId: string): AccountGroup[] {
    return availableGroups.value.filter((group) =>
      group.accounts.includes(accountId)
    )
  }

  function handleGroupsClick(group: AccountGroup) {
    if (selectedGroups.value.includes(group)) {
      selectedGroups.value.splice(selectedGroups.value.indexOf(group), 1)
    } else {
      selectedGroups.value.push(group)
    }
  }

  function getSelectedGroupsIds(accountId: string): string[] {
    return getGroupsByAccountId(accountId).map((group) => group._id)
  }

  async function removePersonFromGroup(groupId: string, personId: string) {
    await UserGroupsService.removeAccountFromAccountGroupApiV1ManagementGroupsAccountGroupIdAccountsAccountIdDelete(
      {
        accountGroupId: groupId,
        accountId: personId,
      }
    )
    loadGroups()
  }

  async function addPersonToAccountGroup(groupId: string, personId: string) {
    await UserGroupsService.addAccountToAccountGroupApiV1ManagementGroupsAccountGroupIdAccountsAccountIdPost(
      {
        accountGroupId: groupId,
        accountId: personId,
      }
    )
    loadGroups()
  }

  function handleCreateGroup() {
    router.push({
      name: 'Groups',
      query: {
        create: 'true',
      },
    })
  }

  // ##############################################
  // Tag related stuff
  // ##############################################

  const availableTags = ref<Tag[]>([])
  const selectedTags = ref<Tag[]>([])
  const dialogManageVisible = ref(false)
  const dialogCreateVisible = ref(false)

  function removeTagFromPerson(
    tagId: string,
    tagsBefore: string[],
    personId: string
  ) {
    const updateTags = tagsBefore.filter((existing) => existing != tagId)
    const acc: AccountUpdateIn = { tags: updateTags, _id: personId }
    PersonStore.updatePerson(personId, acc)
  }

  function addTagToPerson(
    tagId: string,
    tagsBefore: string[],
    personId: string
  ) {
    const updateTags = new Array(...tagsBefore)
    updateTags.push(tagId)
    const acc: AccountUpdateIn = { tags: updateTags, _id: personId }

    PersonStore.updatePerson(personId, acc)
  }

  function handleTagClick(tag: Tag) {
    if (selectedTags.value.includes(tag)) {
      selectedTags.value.splice(selectedTags.value.indexOf(tag), 1)
    } else {
      selectedTags.value.push(tag)
    }

    filter.value.tags = selectedTags.value.map((tag) => tag._id)
  }

  function getTagsByIds(ids: string[]): Tag[] {
    return availableTags.value.filter((tag) => ids.includes(tag._id))
  }

  function loadTags() {
    TagsService.getTagsApiV1ManagementTagsGet({}).then((tags) => {
      availableTags.value = tags
    })
  }

  const newTagPerson = ref<Account | null>(null)

  function openCreateTagDialog(person: Account) {
    dialogCreateVisible.value = true
    newTagPerson.value = person
  }

  function handleNewTagCreated(tag: Tag) {
    loadTags()
    if (newTagPerson.value) {
      addTagToPerson(
        tag._id,
        newTagPerson.value?.tags || [],
        newTagPerson.value?._id
      )
      newTagPerson.value = null
    }
  }

  const columns = computed<Column<EnrichedAccount>[]>(() => [
    {
      key: 'name',
      label: i18n.t('views.users.table.name'),
      sortable: true,
      sortFn: (a: EnrichedAccount, b: EnrichedAccount) =>
        useSortTableColumn(a.name ?? '', b.name ?? '', 'desc'),
      width: 0.9,
      footer: personFooterCount,
      loader: true,
    },
    {
      key: 'email',
      label: i18n.t('views.users.table.email'),
      sortable: true,
      sortFn: (a: Account, b: Account) =>
        useSortTableColumn(a.email, b.email, 'asc'),
      width: 1,
      loader: true,
    },
    {
      key: 'employees',
      label: i18n.t('employees'),
      sortable: true,
      width: 0.45,
      loader: true,
      sortFn: (a: EnrichedAccount, b: EnrichedAccount) =>
        useSortTableColumn(
          a.employee_ids?.length || 0,
          b.employee_ids?.length || 0,
          'desc'
        ),
    },
    {
      key: 'groups',
      label: i18n.t('groups'),
      sortable: false,
      width: 0.8,
      loader: true,
    },
    {
      key: 'tags',
      label: i18n.t('tags'),
      sortable: false,
      width: 0.8,
    },
    {
      key: 'applicationCount',
      label: i18n.t('apps'),
      sortable: true,
      width: '100px',
      loader: true,
      sortFn: (a: EnrichedAccount, b: EnrichedAccount) => {
        const aCount: number = a.application_count || 0
        const bCount: number = b.application_count || 0
        return useSortTableColumn(aCount, bCount, 'desc')
      },
    },
    {
      key: 'action',
      label: '',
      width: '50px',
      sortable: false,
    },
  ])

  onMounted(() => {
    loadTags()
    fetchSoftwareLicenses()
    loadGroups()
  })
</script>
<style scoped>
  .tag-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 5px;
    padding: 0.5rem 0 0.5rem 0;
    align-items: center;
    width: 100%;
  }

  .welcome-text {
    font-size: 1.5rem;
    font-weight: 600;
  }

  .el-scrollbar__view {
    width: 100%;
  }

  .selection-edit-dropdown-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    padding: 20px 10px;
  }

  .loading-select-button {
    color: var(--el-disabled-text-color);
  }

  .loading-select-button :hover {
    color: var(--el-disabled-text-color);
  }

  .template-wrapper {
    display: grid;
    width: 100%;
    height: 100%;
    grid-template-columns: 1fr 1fr;
    grid-gap: 5px;
    max-height: 45px;
    overflow: auto;
  }

  .tag {
    cursor: pointer;
  }

  .delete-person {
    color: var(--el-color-danger);
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .employee-indicator {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    justify-content: center;
    min-width: 44px;
    padding: 4px 8px;
    border-radius: 999px;
    background: var(--el-fill-color);
    color: var(--el-text-color-secondary);
    font-weight: 600;
    font-size: 0.85rem;
  }

  .employee-indicator--linked {
    background: rgba(76, 175, 80, 0.1);
    color: var(--el-color-success);
    box-shadow: inset 0 0 0 1px rgba(76, 175, 80, 0.15);
  }

  .application-count {
    width: 100%;
    text-align: left;
  }

  .tag-add-icon {
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .tag-wrapper:hover .tag-add-icon {
    opacity: 1;
  }
</style>
